import { BareEvent, Event } from '../../shared/types/event';
import { HourFormat, BrandCommonTextValues } from '../types/brand';
import { EventDisplayOption, EventTemplate } from '../types/eventTemplate';
import { Day, Time } from '../types/time';
import {
  timeToString,
  addMinutesToTime,
  TimeStringFormat,
  timeToMinutes,
  DEFAULT_TIMESTR_FORMAT,
} from './time';
import { BareVenueTemplate, VenueTemplate } from '../../shared/types/venueTemplate';
import { StaffTemplate } from '../../shared/types/staffTemplate';
import { mapBy, notUndefined } from './functional';
import { BareStaffAllocation, StaffAllocation } from '../../shared/types/staffAllocation';
import { safeIdCompare } from './utils';
import { ContentTemplateParams } from '../types/contentTemplate';
import { getVenueDisplayName, getVenueLocation, VENUE_STRING_STYLE } from './venue';

// Keep in sync with types
// TODO: Can this be done in a better way so that we at least get a compiler error for missing
// values?
// TODO: ^^ Yes, with const assertions, once we upgrade to TS3.4
//       https://stackoverflow.com/a/55505556

export const EVENT_DISPLAY_TYPES: EventDisplayOption[] = [
  'show-finish-time',
  'do-not-show-finish-time',
  'show-as-24/7',
  'ends-late',
  'override-start-time',
];

type EventDisplayMap = {
  [key in EventDisplayOption]: {
    label: string;
    value: EventDisplayOption;
  };
};

export const EVENT_DISPLAY_MAP: EventDisplayMap = {
  'show-finish-time': {
    label: 'Show finish time',
    value: 'show-finish-time',
  },
  'do-not-show-finish-time': {
    label: 'Start time only',
    value: 'do-not-show-finish-time',
  },
  'show-as-24/7': {
    label: "Time as '24/7'",
    value: 'show-as-24/7',
  },
  'ends-late': {
    label: "End time as 'Late'",
    value: 'ends-late',
  },
  'override-start-time': {
    label: 'Override start time',
    value: 'override-start-time',
  },
};

export function isValidEventDisplayOption(str: string): boolean {
  return (EVENT_DISPLAY_TYPES as string[]).includes(str);
}

/**
 * Given an event start time and duration, and an event and template display options, return a
 * representation of the event's time (as start - end) applying the specified format
 *
 */
export function generateFormattedTimeString(
  startTime: Time,
  duration: number,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat,
  commonTexts: BrandCommonTextValues,
  eventDisplay?: EventDisplayOption,
  eventTemplateDisplay?: EventDisplayOption,
  defaultDisplay: EventDisplayOption = 'show-finish-time'
) {
  let displayOptions = getDisplayOptions(eventDisplay, eventTemplateDisplay, defaultDisplay);
  return generateTimeStringSimple(
    startTime,
    duration,
    hourFormat,
    timeFormat,
    displayOptions,
    commonTexts
  );
}

/**
 * Same as `generateFormattedTimeString`, but applying the defaults of `timeString` (Just for
 * compatibility reasons)
 *
 */
export function generateTimeString(
  startTime: Time,
  duration: number,
  hourFormat: HourFormat,
  commonTexts: BrandCommonTextValues,
  eventDisplay?: EventDisplayOption,
  eventTemplateDisplay?: EventDisplayOption,
  defaultDisplay: EventDisplayOption = 'show-finish-time',
  params?: ContentTemplateParams
) {
  let displayOptions = getDisplayOptions(eventDisplay, eventTemplateDisplay, defaultDisplay);
  return generateTimeStringSimple(
    startTime,
    duration,
    hourFormat,
    DEFAULT_TIMESTR_FORMAT,
    displayOptions,
    commonTexts,
    params
  );
}

export function generateExtendedTimeDescription(
  startTime: Time,
  duration: number,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat,
  words: BrandCommonTextValues,
  eventDisplay?: EventDisplayOption,
  eventTemplateDisplay?: EventDisplayOption,
  defaultDisplay: EventDisplayOption = 'show-finish-time'
): ExtendedTimeDescription {
  let displayOptions = getDisplayOptions(eventDisplay, eventTemplateDisplay, defaultDisplay);
  return generateExtendedTimeDescriptionInternal(
    startTime,
    duration,
    hourFormat,
    timeFormat,
    displayOptions,
    words
  );
}

/**
 * Same as `generateTimeString`, but only return the display options
 *
 */
export function generateDisplayOptions(
  eventDisplay?: EventDisplayOption,
  eventTemplateDisplay?: EventDisplayOption,
  defaultDisplay: EventDisplayOption = 'show-finish-time'
) {
  return getDisplayOptions(eventDisplay, eventTemplateDisplay, defaultDisplay);
}

// Take the instance display options, otherwise the template's, otherwise a default
// finish time
function getDisplayOptions(
  eventDisplay: EventDisplayOption | undefined,
  templateDisplay: EventDisplayOption | undefined,
  defaultDisplay: EventDisplayOption
): EventDisplayOption {
  return eventDisplay || templateDisplay || defaultDisplay;
}

export function getBasicTimeString<T extends { startTime: Time; duration: number }>(
  event: T,
  hourFormat: HourFormat
) {
  const startTimeStr = timeToString(event.startTime, hourFormat, DEFAULT_TIMESTR_FORMAT);
  const endTime = addMinutesToTime(event.startTime, event.duration);
  const endTimeStr = timeToString(endTime, hourFormat, DEFAULT_TIMESTR_FORMAT);
  return `${startTimeStr} - ${endTimeStr}`;
}

/**
 * Returns an extended representation of all the parts of an event's  time.
 *
 * The resulting object should be used as follows:
 *   - Use the (start|end)TimeString value as a higher precedence value.
 *   - If that is not defined, then use the (start|end)Time, respectively
 *   - Alternatively, use `timeString` formatted string
 */
export interface ExtendedTimeDescription {
  startTime: Time | null;
  endTime: Time | null;
  altStartTimeString: string | null;
  altEndTimeString: string | null;
  timeString: string;
}

/**
 * Similar to `makeEventSorter`, but don't assume we have a template or a name,
 * so just sort by date. This may be used with a model with duration or endTime
 *
 */
interface SortableEvent {
  startTime: Time;
  duration?: number;
  endTime?: Time;
}

export function makeBareEventSorter(dayStart: number) {
  return (e1: SortableEvent, e2: SortableEvent) => {
    let startTime1 = timeToMinutes(e1.startTime, dayStart);
    let startTime2 = timeToMinutes(e2.startTime, dayStart);

    if (startTime1 !== startTime2) return startTime1 - startTime2;

    // duration case
    if (e1.duration !== undefined && e2.duration !== undefined) {
      return e1.duration - e2.duration;
    }

    // end time case
    if (e1.endTime !== undefined && e2.endTime !== undefined) {
      let endTime1 = timeToMinutes(e1.endTime, dayStart);
      let endTime2 = timeToMinutes(e2.endTime, dayStart);

      return endTime1 - endTime2;
    }

    // default case, for backup
    return startTime1 - startTime2; // duplicate
  };
}

function generateExtendedTimeDescriptionInternal(
  eventStartTime: Time,
  duration: number,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat,
  displayOptions: EventDisplayOption,
  words: BrandCommonTextValues,
  params?: ContentTemplateParams
): ExtendedTimeDescription {
  let timeString = '';
  let altStartTimeString: string | null = null;
  let altEndTimeString: string | null = null;
  let startTime: Time | null = null;
  let endTime: Time | null = null;

  let eventEndTime = addMinutesToTime(eventStartTime, duration);

  let parsedStartTimeString = timeToString(eventStartTime, hourFormat, timeFormat);
  let parsedEndTimeString = timeToString(eventEndTime, hourFormat, timeFormat);

  const { ends_late, twenty_four_seven, override_start_time } = words;

  if (displayOptions === 'show-finish-time') {
    startTime = eventStartTime;
    endTime = eventEndTime;
    timeString = `${parsedStartTimeString} - ${parsedEndTimeString}`;
  } else if (displayOptions === 'ends-late') {
    startTime = eventStartTime;
    altEndTimeString = ends_late;
    timeString = `${parsedStartTimeString} - ${ends_late}`;
  } else if (displayOptions === 'do-not-show-finish-time') {
    startTime = eventStartTime;
    timeString = parsedStartTimeString;
  } else if (displayOptions === 'show-as-24/7') {
    altStartTimeString = twenty_four_seven;
    timeString = twenty_four_seven;
  } else if (displayOptions === 'override-start-time') {
    timeString = `${override_start_time} - ${parsedEndTimeString}`;
    altStartTimeString = override_start_time;
  }

  // Apply custom params filters
  if (params) {
    const { eventRundownRemoveTimeDash, eventRundownHideEndTime } = params;

    if (eventRundownRemoveTimeDash) {
      timeString = timeString.replace(/\s+\-\s+/, ' ');
    }

    if (eventRundownHideEndTime) {
      endTime = null;
      timeString = parsedStartTimeString;
    }
  }

  return {
    startTime,
    endTime,
    altStartTimeString,
    altEndTimeString,
    timeString,
  };
}

/**
 * Same as the previous, but only uses the `timeString` representation. This is useful if the client
 * consuming the result of the function just needs to use a string rather than having to parse all
 * the possible start / end time combinations
 */
function generateTimeStringSimple(
  startTime: Time,
  duration: number,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat,
  displayOptions: EventDisplayOption,
  commonTexts: BrandCommonTextValues,
  params?: ContentTemplateParams
): string {
  let { timeString } = generateExtendedTimeDescriptionInternal(
    startTime,
    duration,
    hourFormat,
    timeFormat,
    displayOptions,
    commonTexts,
    params
  );
  return timeString;
}

function populateStaffAllocation(
  bareStaff: BareStaffAllocation,
  staffTemplate: StaffTemplate
): StaffAllocation {
  return {
    ...bareStaff,
    template: staffTemplate,
  };
}

export function populateEvent(
  bareEvent: BareEvent,
  eventTemplates: EventTemplate[],
  venueTemplates: VenueTemplate[],
  staffTemplates: StaffTemplate[],
  day: Day
): Event {
  let template = eventTemplates.find(et => safeIdCompare(et._id, bareEvent.template));
  let venue = venueTemplates.find(vt => safeIdCompare(vt._id, bareEvent.venue));

  if (!template) throw new Error(`Could not find event template ${bareEvent.template}`);
  if (!venue) throw new Error(`Could not find venue template ${bareEvent.venue}`);

  let staffAllocations = bareEvent.staffAllocations.map(s => {
    let staffTemplate = staffTemplates.find(st => safeIdCompare(st._id, s.template));
    if (!staffTemplate) throw new Error(`Could not find staff template ${s.template}`);

    return populateStaffAllocation(s, staffTemplate);
  });

  const highlightsMap = mapBy(template.brand.eventHighlights || [], '_id');

  return {
    ...bareEvent,
    staffAllocations,
    template,
    venue,
    day,
    highlights: (bareEvent.highlights || []).map(id => highlightsMap[id]).filter(notUndefined),
  };
}

/**
 * Sort two event-like items for the list.
 * Events are sorted by start time, then end time, then by event title
 */
export function makeEventSorter(dayStart: number) {
  return (
    e1: Pick<Event, 'startTime' | 'duration'> & { template?: { title: string } | string },
    e2: Pick<Event, 'startTime' | 'duration'> & { template?: { title: string } | string }
  ) => {
    let startTime1 = timeToMinutes(e1.startTime, dayStart);
    let startTime2 = timeToMinutes(e2.startTime, dayStart);

    let duration1 = e1.duration;
    let duration2 = e2.duration;

    if (startTime1 !== startTime2) return startTime1 - startTime2;
    if (duration1 !== duration2) return duration1 - duration2;

    // ugly, but this function is used across multiple variations of event
    // the cost of multiple event sorters is worse than this
    if (typeof e1.template === 'string' || typeof e2.template === 'string') {
      return ''.localeCompare('');
    }

    return (e1.template?.title ?? '').localeCompare(e2.template?.title ?? '');
  };
}

export type EventVenueDisplay = {
  name: string;
  location: string;
  label: string;
  level: number;
  venue_location: string;
  deck: string;
  venueString: string;
};

export function getEventVenueDisplay(
  event: Event | BareEvent,
  venTemplate: VenueTemplate | BareVenueTemplate,
  commonTexts: BrandCommonTextValues,
  venueStringType?: VENUE_STRING_STYLE
): EventVenueDisplay {
  const label = commonTexts.deck;

  const name = event.venueDisplayName || venTemplate.name;
  const deck = event.deckDisplayName || getVenueDisplayName(venTemplate, commonTexts);

  const location = [venueStringType !== 'no-label' ? label : undefined, deck]
    .filter(notUndefined)
    .join(' ');

  return {
    name,
    location,
    deck,
    level: venTemplate.level,
    venue_location: getVenueLocation(venTemplate, commonTexts),
    label,
    venueString: [name, venueStringType !== 'name-only' ? location : undefined]
      .filter(notUndefined)
      .join(', '),
  };
}
